import * as ContentServices from '@/services/ContentServices';
import pageSchemas from '@/content/page-schemas.generated.json';
import { isRecord } from '@/content/schema';

/** A page of the site that a link field can point to. */
export interface SitePage {
  path: string;
  name: string;
}

/** The fixed pages of the site (web/src/app). */
const STATIC_PAGES: SitePage[] = [
  { path: '/', name: 'Home' },
  { path: '/factchecks', name: 'Alle factchecks' },
  { path: '/dossiers', name: 'Dossiers' },
  { path: '/downloads-en-tools', name: 'Downloads & tools' },
  { path: '/games', name: 'Games' },
  { path: '/games/mens-of-machine', name: 'Game: Mens of machine?' },
  { path: '/games/headline-of-headlie', name: 'Game: Headline of Headlie' },
  { path: '/woorden', name: 'Woord van de week' },
  { path: '/quotes', name: 'Quote van de week' },
  { path: '/infographics', name: 'Infographic van de maand' },
  { path: '/factchecks-van-de-week', name: 'Factchecks van de week' },
  { path: '/desinformatietechnieken', name: 'Desinformatietechnieken' },
  { path: '/eerste-hulp', name: 'Eerste hulp bij' },
  { path: '/woordenboek', name: 'Verklarende woordenlijst' },
  { path: '/hoe-check-je-zelf-een-bericht', name: 'Hoe check je zelf een bericht?' },
  { path: '/hoe-herken-je-ai-beelden', name: 'Hoe herken je AI-beelden?' },
  { path: '/wat-is-desinformatie/wat-is-het', name: 'Wat is het?' },
  { path: '/wat-is-desinformatie/desinformatie-vs-misinformatie', name: 'Desinformatie vs misinformatie' },
  { path: '/wat-is-desinformatie/waarom-geloven-we-het', name: 'Waarom geloven we het?' },
  { path: '/workshops', name: 'Workshops' },
  { path: '/over-ons', name: 'Over ons' },
  { path: '/steun-ons', name: 'Doneer' },
  { path: '/doe-mee', name: 'Doe mee' },
  { path: '/nieuwsbrief', name: 'Nieuwsbrief' },
  { path: '/blog', name: 'Blog' },
  { path: '/contact', name: 'Contact' },
  { path: '/factcheck-suggereren', name: 'Tip ons' },
  { path: '/privacy', name: 'Privacybeleid' },
  { path: '/cookies', name: 'Cookiebeleid' },
  { path: '/toegankelijkheid', name: 'Toegankelijkheid' },
];

interface NamedSlug {
  slug: string;
  title: string;
}

function namedSlugs(value: unknown): NamedSlug[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((item) => (typeof item.slug === 'string' && item.slug
    ? [{ slug: item.slug, title: typeof item.title === 'string' ? item.title : item.slug }]
    : []));
}

/** Series and stories of The bigger picture, from the saved page or else the site's defaults. */
async function biggerPicturePages(): Promise<SitePage[]> {
  let content: unknown = null;
  try {
    const result = await ContentServices.GetContent('the-bigger-picture');
    content = result ? result.content : null;
  } catch (error) {
    content = null;
  }
  const source = isRecord(content) ? content : pageSchemas['the-bigger-picture'].defaults;
  const pages: SitePage[] = [];
  const series = isRecord(source) && Array.isArray(source.series) ? source.series : [];
  for (const entry of series) {
    const [named] = namedSlugs([entry]);
    if (!named || !isRecord(entry)) continue;
    pages.push({ path: `/the-bigger-picture/${named.slug}`, name: `The bigger picture: ${named.title}` });
    for (const story of namedSlugs(entry.items)) {
      pages.push({ path: `/the-bigger-picture/${named.slug}/${story.slug}`, name: `${named.title}: ${story.title}` });
    }
  }
  return pages;
}

let loading: Promise<SitePage[]> | null = null;

/** All pages a link can point to; loaded once per visit of the CMS. */
export function sitePages(): Promise<SitePage[]> {
  if (!loading) loading = biggerPicturePages().then((dynamic) => [...STATIC_PAGES, ...dynamic]);
  return loading;
}
